import { useState } from 'react';
import { readDrillParams } from '../utils/drilldown';
import { useAddressEntry } from './useLinkedSearch';

/**
 * Shared filter + pagination STATE for the report/list pages
 * (Grooming, Mood, Random Attendance Logs, Daily Attendance Logs).
 *
 * Each of those pages inlined the SAME block of state verbatim:
 *   - searchQuery / setSearchQuery
 *   - pageSize / setPageSize
 *   - currentPage / setCurrentPage
 *   - the date-range "pending → applied" model:
 *       startDate / endDate (pending, editable inputs)
 *       appliedStartDate / appliedEndDate (committed copy the fetch watches)
 *   - hasPendingDateChanges = startDate !== appliedStartDate || endDate !== appliedEndDate
 *   - applyDateRange = guard on both dates, then commit applied = pending
 *     AND reset to page 1 (a date change is a fresh query).
 *
 * This is PURE STATE — behaviour-preserving — and is INDEPENDENT of how each
 * page paginates (server-side vs client-side). The hook owns ONLY the state the
 * pages share byte-for-byte; page-specific extra state (emoji filter, status
 * filter, debounced search, abort refs, …) and ALL fetch/render logic stay on
 * the page.
 *
 * Date fields use the generic startDate/endDate naming. Pages that locally call
 * these fromDate/toDate (Daily Attendance) just rename on destructure.
 *
 * LINKS. A page that feeds a link's values into the `initial…` options (Daily
 * Attendance, Mood: utils/drilldown's employeeCode / q / startDate / endDate) used to
 * have them read once, on mount. A second link landing on the page while it was open
 * (search picks a result in the same tab) then changed the address and nothing else.
 * So when the person arrives at another address of the same page, the options are read
 * again, as a fresh open of that address would read them:
 *   - search: filled with `initialSearchQuery` when the link gives one; emptied when it
 *     gives none and the box still holds the previous link's text untouched. Text the
 *     person typed stays.
 *   - dates: pending AND applied set together from `initialStartDate` / `initialEndDate`
 *     when the address names startDate or endDate, or named one and no longer does.
 *     Dates no address named are the person's own (or the page's default) and stay: a
 *     default that moved on at midnight must not replace a range somebody picked.
 * An address the page replaces for itself is not an arrival and re-reads nothing (see
 * useAddressEntry). With no router around the page there is nothing to arrive at.
 */
interface UseReportFiltersOptions {
  /** Initial pending + applied start date (e.g. getLocalYesterdayDateString()). */
  initialStartDate: string;
  /** Initial pending + applied end date. */
  initialEndDate: string;
  /** Initial search query. Defaults to ''. */
  initialSearchQuery?: string;
  /** Initial rows-per-page. Defaults to 50. */
  initialPageSize?: number;
  /** Initial page (1-based). Defaults to 1. */
  initialPage?: number;
}

interface UseReportFiltersResult {
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;

  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;

  startDate: string;
  setStartDate: React.Dispatch<React.SetStateAction<string>>;
  endDate: string;
  setEndDate: React.Dispatch<React.SetStateAction<string>>;
  appliedStartDate: string;
  setAppliedStartDate: React.Dispatch<React.SetStateAction<string>>;
  appliedEndDate: string;
  setAppliedEndDate: React.Dispatch<React.SetStateAction<string>>;

  hasPendingDateChanges: boolean;
  applyDateRange: () => void;
}

export function useReportFilters({
  initialStartDate,
  initialEndDate,
  initialSearchQuery = '',
  initialPageSize = 50,
  initialPage = 1,
}: UseReportFiltersOptions): UseReportFiltersResult {
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [appliedStartDate, setAppliedStartDate] = useState(initialStartDate);
  const [appliedEndDate, setAppliedEndDate] = useState(initialEndDate);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);

  const [pageSize, setPageSize] = useState(initialPageSize);
  const [currentPage, setCurrentPage] = useState(initialPage);

  const entry = useAddressEntry();
  const namesDates = (search: string): boolean => {
    const drill = readDrillParams(search);
    return !!(drill.startDate || drill.endDate);
  };
  // What the address last arrived at gave the page, to tell what the next one changes.
  const [seen, setSeen] = useState(() => ({
    key: entry?.key,
    searchQuery: initialSearchQuery,
    datesNamed: namesDates(entry?.search ?? ''),
  }));
  // Set while rendering, not in an effect: pending and applied dates must land in the
  // same render, or Apply would show a change nobody made and the list would be drawn
  // once more for the previous link.
  if (entry && seen.key !== entry.key) {
    const datesNamed = namesDates(entry.search);
    if (entry.arrived) {
      if (initialSearchQuery) setSearchQuery(initialSearchQuery);
      else if (seen.searchQuery && searchQuery === seen.searchQuery) setSearchQuery('');
      if (datesNamed || seen.datesNamed) {
        setStartDate(initialStartDate);
        setEndDate(initialEndDate);
        setAppliedStartDate(initialStartDate);
        setAppliedEndDate(initialEndDate);
        setCurrentPage(1); // new dates are a fresh query, as in applyDateRange
      }
      setSeen({ key: entry.key, searchQuery: initialSearchQuery, datesNamed });
    } else {
      setSeen({ ...seen, key: entry.key });
    }
  }

  const hasPendingDateChanges =
    startDate !== appliedStartDate || endDate !== appliedEndDate;

  const applyDateRange = () => {
    if (!startDate || !endDate) return;
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setCurrentPage(1); // a date change is a fresh query — go back to page 1
  };

  return {
    searchQuery,
    setSearchQuery,
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    appliedStartDate,
    setAppliedStartDate,
    appliedEndDate,
    setAppliedEndDate,
    hasPendingDateChanges,
    applyDateRange,
  };
}
